import { BMInputField } from "@traveloka/district-bloom";
import IcSystemChevronDown from "@traveloka/icon-kit-web/react/IcSystemChevronDown";
import { useCallback, useMemo, useRef, useState } from "react";
import { Dimensions, Platform, View } from "react-native";

import { appendTestId } from "../../utils";
import BMView from "../View/BMView";
import BMDropdownDesktopOptions from "./components/BMDropdownDesktopOptions";
import BMDropdownMobileOptions from "./components/BMDropdownMobileOptions";
import { useGetValue } from "./hooks";
import { BMDropdownFieldProps, BMDropdownOptionsValue } from "./types";

const IS_MOBILE = Dimensions.get("window").width < 768;

export default function BMDropdownField<T = string>({
  value,
  label = "",
  option,
  zIndex = 9,
  isRequired = false,
  variant = "DEFAULT",
  searchable,
  placeholder,
  optionsSection,
  getOptionValue,
  getOptionLabel,
  onChange,
  testID,
  ...rest
}: BMDropdownFieldProps<T>) {
  const [isOptionVisible, setIsOptionVisible] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef<View>(null);

  const getValue = useGetValue(getOptionValue);

  const selected = useMemo(() => {
    if (!value) return null;
    const selectedValue = getValue(value);
    return (
      option.items.find((item) => getValue(item.value) === selectedValue) ||
      null
    );
  }, [option.items, value, getValue]);

  const optionsWithValueOnTop = useMemo((): BMDropdownOptionsValue<T>[] => {
    if (!option.items) return [];
    if (!value) return option.items;
    const selectedValue = getValue(value);
    return [
      ...(selected ? [selected] : []),
      ...option.items.filter((item) => getValue(item.value) !== selectedValue),
    ];
  }, [selected, option.items, getValue, value]);

  const handleOptionChange = useCallback(
    (selectedOption: BMDropdownOptionsValue<T> | null) => {
      setIsOptionVisible(false);
      onChange?.(selectedOption?.value || null);
      setQuery("");
    },
    [onChange]
  );

  function toggleOptionVisible() {
    setIsOptionVisible((prev) => !prev);
  }

  const selectedLabel = useMemo(() => {
    if (!selected) return "";
    return getOptionLabel ? getOptionLabel(selected) : selected.label;
  }, [selected, getOptionLabel]);

  const showDesktopOptions = Platform.OS === "web" && !IS_MOBILE;
  const showMobileOptions = Platform.OS !== "web" || IS_MOBILE;

  const optionsProps = {
    selected,
    show: isOptionVisible,
    isRadio: variant === "RADIO",
    option: {
      ...option,
      items: optionsWithValueOnTop,
    },
    searchable,
    getOptionValue,
    onChange: handleOptionChange,
  };

  return (
    <BMView style={{ zIndex }} ref={wrapperRef}>
      <BMInputField
        value={isOptionVisible ? query : selectedLabel}
        label={`${label}${isRequired ? " *" : ""}`}
        variant={showMobileOptions || !searchable ? "DROPDOWN" : "FIELD"}
        buttonInsetType="BUTTON_ICON"
        buttonInsetProps={{
          Icon: IcSystemChevronDown,
          onPress: toggleOptionVisible,
        }}
        placeholder={placeholder}
        testID={appendTestId(testID, "input")}
        {...(showMobileOptions || !searchable
          ? { onDropdownToggle: toggleOptionVisible }
          : {
              onFocus: () => setIsOptionVisible(true),
              onChange: (e: any) => setQuery(e.nativeEvent.text),
            })}
        {...rest}
      />
      {showDesktopOptions && (
        <BMDropdownDesktopOptions
          {...optionsProps}
          query={query}
          wrapperRef={wrapperRef}
          wrapperStyles={optionsSection?.wrapperStyles}
        />
      )}
      {showMobileOptions && (
        <BMDropdownMobileOptions
          {...optionsProps}
          optionsSection={optionsSection}
          onClose={() => setIsOptionVisible(false)}
        />
      )}
    </BMView>
  );
}
